/* Dawn disclosure lifecycle, with desktop hover and section-reload support. */
class KnHeaderMenu extends HeaderMenu {
  connectedCallback() {
    this.header = this.closest('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.panel = this.summary.nextElementSibling;
    this.nav = this.closest('nav');
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.hover = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };

    this.addEventListener('pointerenter', (event) => {
      if (!this.desktop.matches || !this.hover.matches || event.pointerType === 'touch') return;
      this.clearTimers();
      this.openTimer = setTimeout(() => this.open(), 130);
    }, options);
    this.addEventListener('pointerleave', () => {
      this.clearTimers();
      // Keyboard focus keeps the disclosure available while reading/tabbing.
      if (!this.contains(document.activeElement)) {
        this.closeTimer = setTimeout(() => this.close(), 220);
      }
    }, options);
    this.summary.addEventListener('click', () => this.clearTimers(), options);
    this.nav.addEventListener('pointerover', (event) => {
      const item = event.target.closest('.kn-desktop-nav__item');
      if (item && !this.contains(item) && !this.contains(document.activeElement)) this.close();
    }, options);
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.close();
    }, options);
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.clearTimers();
      if (!this.mainDetailsToggle.open) return;
      const restoreFocus = this.contains(document.activeElement);
      this.close();
      if (restoreFocus) {
        event.preventDefault();
        this.summary.focus();
      }
    }, options);
    this.desktop.addEventListener('change', () => this.close(), options);
    window.addEventListener('resize', () => this.updatePosition(), options);
    window.addEventListener('scroll', () => this.updatePosition(), { ...options, passive: true });
    document.addEventListener('shopify:block:select', (event) => {
      if (this.contains(event.target)) this.open();
    }, options);
    this.summary.setAttribute('aria-expanded', String(this.mainDetailsToggle.open));
    this.updatePosition();
    this.updateBackdrop();
  }

  disconnectedCallback() {
    this.close();
    this.listeners.abort();
  }

  clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  open() {
    if (!this.desktop.matches || !this.isConnected) return;
    this.clearTimers();
    this.nav.querySelectorAll('kn-header-menu').forEach((menu) => {
      if (menu !== this) menu.close();
    });
    this.mainDetailsToggle.open = true;
    this.summary.setAttribute('aria-expanded', 'true');
    this.header.preventHide = true;
    this.updatePosition();
    this.updateBackdrop();
  }

  close() {
    this.clearTimers();
    super.close();
    if (this.header) {
      this.header.preventHide = Boolean(this.header.querySelector('kn-header-menu details[open], header-menu details[open]'));
    }
    this.updateBackdrop();
  }

  onToggle() {
    super.onToggle();
    if (this.header) {
      this.header.preventHide = Boolean(this.header.querySelector('kn-header-menu details[open], header-menu details[open]'));
    }
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', String(this.mainDetailsToggle.open));
    if (this.mainDetailsToggle.open) this.updatePosition();
    this.updateBackdrop();
  }

  updateBackdrop() {
    const backdrop = this.nav?.querySelector('.kn-mega-menu__backdrop');
    if (!backdrop) return;
    backdrop.classList.toggle('is-active', this.desktop.matches && Boolean(this.nav.querySelector('kn-header-menu details[open]')));
  }

  updatePosition() {
    if (!this.panel || !this.desktop.matches) return;
    const bottom = this.header.getBoundingClientRect().bottom;
    this.nav.style.setProperty('--kn-menu-header-bottom', `${Math.max(0, bottom)}px`);
    this.summary.style.setProperty('--kn-menu-trigger-gap', `${Math.max(0, bottom - this.summary.getBoundingClientRect().bottom)}px`);
    if (!this.mainDetailsToggle.open) return;
    this.panel.style.setProperty('--kn-menu-available-height', `${Math.max(0, window.innerHeight - bottom - 8)}px`);
  }
}

if (!customElements.get('kn-header-menu')) customElements.define('kn-header-menu', KnHeaderMenu);
